import React, { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';

const SaveBar = ({ saving, onSave, onCancel }) => (
  <div className="flex gap-2 pt-1">
    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60"
    >
      {saving ? 'Enregistrement…' : 'Enregistrer'}
    </button>
    <button
      type="button"
      onClick={onCancel}
      disabled={saving}
      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
    >
      Annuler
    </button>
  </div>
);

/** Stratégie d'un cercle : on modifie la liste au calme, puis on enregistre une fois. */
export const StrategyEditor = ({ items, onSave, onClose }) => {
  const [draft, setDraft] = useState(() => (items || []).map((item) => ({ ...item })));
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);
  const newRef = useRef(null);

  useEffect(() => {
    newRef.current?.focus();
  }, []);

  const save = async () => {
    const extra = newItem.trim() ? [{ content: newItem.trim(), updated_at: new Date().toISOString() }] : [];
    const before = new Map((items || []).map((item) => [item.content, item.updated_at]));
    const cleaned = [...draft, ...extra]
      .map((item) => ({ ...item, content: (item.content || '').trim() }))
      .filter((item) => item.content)
      .map((item) => (before.has(item.content) ? item : { ...item, updated_at: new Date().toISOString() }));
    setSaving(true);
    const ok = await onSave(cleaned);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div className="space-y-2">
      {draft.map((item, index) => (
        <div key={`${index}-${item.updated_at || ''}`} className="flex items-center gap-2">
          <input
            value={item.content}
            onChange={(e) => setDraft((prev) => prev.map((it, i) => (i === index ? { ...it, content: e.target.value } : it)))}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))}
            className="p-1 text-slate-400 hover:text-rose-500"
            title="Retirer"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input
          ref={newRef}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newItem.trim()) {
              e.preventDefault();
              setDraft((prev) => [...prev, { content: newItem.trim(), updated_at: new Date().toISOString() }]);
              setNewItem('');
            }
            if (e.key === 'Escape') onClose();
          }}
          className="flex-1 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm focus:border-indigo-300 focus:outline-none"
          placeholder="Nouvelle orientation (Entrée pour ajouter)"
        />
        <Plus size={14} className="text-slate-300" />
      </div>
      <SaveBar saving={saving} onSave={save} onCancel={onClose} />
    </div>
  );
};

/** Leader d'un cercle (une personne ou personne) ou titulaires d'un rôle (plusieurs). */
export const OwnerEditor = ({ isRole, ownerIds, activeEmployees, findEmployee, onSave, onClose }) => {
  const [selected, setSelected] = useState(() => (ownerIds || []).map(Number).filter((id) => !Number.isNaN(id)));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const ids = isRole ? selected : selected.slice(0, 1);
    const ok = await onSave({ owner_id: ids[0] ?? null, owner_ids: ids });
    setSaving(false);
    if (ok) onClose();
  };

  if (!isRole) {
    return (
      <div className="space-y-2">
        <select
          value={selected[0] ?? ''}
          onChange={(e) => setSelected(e.target.value ? [Number(e.target.value)] : [])}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
          autoFocus
        >
          <option value="">Pas de leader</option>
          {activeEmployees.map((emp) => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
        <SaveBar saving={saving} onSave={save} onCancel={onClose} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm text-slate-700">
              {findEmployee(id)?.name || 'Personne inconnue'}
              <button
                type="button"
                onClick={() => setSelected((prev) => prev.filter((other) => other !== id))}
                className="text-slate-400 hover:text-rose-500"
                title="Retirer du rôle"
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">Personne ne tient ce rôle.</p>
      )}
      <select
        value=""
        onChange={(e) => {
          const id = Number(e.target.value);
          if (id && !selected.includes(id)) setSelected((prev) => [...prev, id]);
        }}
        className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none"
      >
        <option value="">Ajouter une personne…</option>
        {activeEmployees
          .filter((emp) => !selected.includes(Number(emp.id)))
          .map((emp) => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
      </select>
      <SaveBar saving={saving} onSave={save} onCancel={onClose} />
    </div>
  );
};
